import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik, type FormikProps, useFormikContext } from "formik";
import * as Yup from "yup";

import {
  useGetGpRejectReasons,
  useRejectDealOpportunityGpV2,
} from "@vega-api/generated/coinvest-deal-opportunity-gp-actions/coinvest-deal-opportunity-gp-actions";
import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import type { DependentOption } from "@vega-api/generated/types/dependentOption";
import { ThemedDropdownField } from "@vega-core/components/Form/Controls/Dropdown/Dropdown/DropdownField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { useToast } from "@vega-core/components/Toast/useToast";
import type { OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

type FormValues = {
  gpRejectReason: string;
  gpRejectSubreason: string;
};

const validationSchema = Yup.object({
  gpRejectReason: Yup.string().required(
    m.coInvestments_declineDeal_gpDeclineReasonRequired(),
  ),
  gpRejectSubreason: Yup.string().required(
    m.coInvestments_declineDeal_gpDeclineSubreasonRequired(),
  ),
});

const LoadingSkeleton = () => (
  <div className="w-60 sm:w-96 flex flex-col gap-4">
    <div className="flex flex-col gap-2">
      <Skeleton className="h-5 w-16" />
      <Skeleton className="h-10 w-full" />
    </div>
    <div className="flex flex-col gap-2">
      <Skeleton className="h-5 w-24" />
      <Skeleton className="h-10 w-full" />
    </div>
  </div>
);

function SubreasonField({ reasons }: { reasons: DependentOption[] }) {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  useEffect(
    function clearSubreasonOnReasonChange() {
      setFieldValue("gpRejectSubreason", "");
    },
    [values.gpRejectReason],
  );

  const matchingOptions = reasons.find(
    (opt) => opt.name === values.gpRejectReason,
  );
  const subreasonOptions: OptionType[] = matchingOptions
    ? matchingOptions.options.map((option) => ({
        label: option,
        value: option,
      }))
    : [];

  return (
    <ThemedDropdownField
      disabled={!values.gpRejectReason}
      isClearable={false}
      label={m.coInvestments_declineDeal_gpSubreasonLabel()}
      name="gpRejectSubreason"
      options={subreasonOptions}
      placeholder={m.coInvestments_declineDeal_gpSubreasonPlaceholder()}
    />
  );
}

type RejectDealModalProps = {
  isOpen: boolean;
  onClose: () => void;
  dealOpportunityId: string;
  dealId: string;
};

export const RejectDealModal = ({
  isOpen,
  onClose,
  dealOpportunityId,
  dealId,
}: RejectDealModalProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();
  const formRef = useRef<FormikProps<FormValues>>(null);

  const {
    data: reasonsData,
    isPending: isReasonsPending,
    isError: isReasonsError,
  } = useGetGpRejectReasons();
  const reasons = reasonsData?.data.reasons ?? [];
  const reasonOptions: OptionType[] = reasons.map((r) => ({
    label: r.name,
    value: r.name,
  }));

  const { mutate: rejectDeal, isPending: isSubmitting } =
    useRejectDealOpportunityGpV2();

  const handleSubmit = ({ gpRejectReason, gpRejectSubreason }: FormValues) => {
    rejectDeal(
      {
        dealOpportunityId,
        data: { gpRejectReason, gpRejectSubreason },
      },
      {
        onSuccess: () => {
          onClose();
          showToast({
            variant: "success",
            title: m.coInvestments_dealDeclined(),
          });
          queryClient.invalidateQueries({
            queryKey: getGetDealTrackingForGpQueryKey(dealId),
          });
        },
        onError: (error) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_declineDeal_errorMessage(),
          });
        },
      },
    );
  };

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainAction: () => formRef.current?.submitForm(),
        mainActionText: m.coInvestments_tracking_declineDeal(),
        mainActionVariant: "destructive",
        disabled: isSubmitting,
        secondaryAction: onClose,
        secondaryActionText: m.btn_cancel(),
        disabledSecondary: isSubmitting,
      }}
      dataTestId="reject-deal-modal"
      description={
        isReasonsError
          ? m.coInvestments_declineDeal_gpReasonsLoadError()
          : undefined
      }
      fluidHeight
      open={isOpen}
      setOpen={onClose}
      title={m.coInvestments_tracking_declineDeal()}
    >
      {isReasonsPending ? <LoadingSkeleton /> : null}
      {reasonsData ? (
        <Formik
          initialValues={{ gpRejectReason: "", gpRejectSubreason: "" }}
          innerRef={formRef}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          <Form className="space-y-4 w-60 sm:w-96">
            <ThemedDropdownField
              isClearable={false}
              label={m.coInvestments_declineDeal_gpReasonLabel()}
              name="gpRejectReason"
              options={reasonOptions}
              placeholder={m.coInvestments_declineDeal_gpReasonPlaceholder()}
            />
            <SubreasonField reasons={reasons} />
          </Form>
        </Formik>
      ) : null}
    </ActionaryModal>
  );
};
